import { Events } from '@ezchops/helpers';
import { WebMidiInputAdapter } from '@ezchops/midi/lib/adapter';
import { chain, clamp } from 'lodash';
export const defaultConfig = {
    id: 'default',
    module: null,
    pads: [
        { pad: 35, note: 36 },
        { pad: 36, note: 36 },
        { pad: 38, note: 40 },
        { pad: 40, note: 40 },
        { pad: 42, note: 42 },
        { pad: 46, note: 46 },
        { pad: 44, note: 44 },
        { pad: 49, note: 49 },
        { pad: 55, note: 49 },
        { pad: 51, note: 51 },
        { pad: 59, note: 51 },
        { pad: 53, note: 53 },
        { pad: 50, note: 48 },
        { pad: 48, note: 48 },
        { pad: 45, note: 45 },
        { pad: 47, note: 45 },
        { pad: 43, note: 43 },
        { pad: 41, note: 43 } // low floor tom
    ]
};
export const defaultRolandConfig = {
    id: 'roland default',
    module: null,
    pads: [
        { pad: 35, note: 36 },
        { pad: 36, note: 36 },
        { pad: 38, note: 40 },
        { pad: 40, note: 40 },
        { pad: 42, note: 42 },
        { pad: 22, note: 42 },
        { pad: 26, note: 42, pedal: [90, 127] },
        { pad: 46, note: 42, pedal: [90, 127] },
        { pad: 44, note: 44 },
        { pad: 26, note: 46, pedal: [0, 89] },
        { pad: 46, note: 46, pedal: [0, 89] },
        { pad: 49, note: 49 },
        { pad: 55, note: 49 },
        { pad: 51, note: 53 },
        { pad: 59, note: 51 },
        { pad: 53, note: 53 },
        { pad: 50, note: 48 },
        { pad: 48, note: 48 },
        { pad: 47, note: 45 },
        { pad: 45, note: 45 },
        { pad: 43, note: 43 },
        { pad: 41, note: 43 } // low floor tom
    ]
};
export class MidiDrum {
    _input;
    _config;
    constructor(input, config) {
        this._input = input;
        this._config = config;
    }
    get input() {
        return this._input;
    }
    createAdapter(clock) {
        return new MidiDrumAdapter(this._input, clock, this.config);
    }
    get module() {
        return WebMidiInputAdapter.inputModule(this._input);
    }
    get config() {
        return this._config;
    }
    get label() {
        return WebMidiInputAdapter.inputLabel(this._input);
    }
}
export class DrumAdapter extends Events.EventEmitter {
    inputAdapter;
}
export class MidiDrumAdapter extends DrumAdapter {
    input;
    clock;
    config;
    messageAdapter;
    inputAdapter;
    constructor(input, clock, config = defaultConfig) {
        super();
        this.input = input;
        this.clock = clock;
        this.config = config;
        this.inputAdapter = new WebMidiInputAdapter(input);
        this.messageAdapter = new DrumMessageAdapter(clock, config);
        this.inputAdapter.addEventListener('noteon', this.handleNoteOn);
        this.inputAdapter.addEventListener('noteoff', this.handleNoteOff);
        this.inputAdapter.addEventListener('controlchange', this.handleControlChange);
        this.inputAdapter.addEventListener('message', this.handleMessage);
    }
    close() {
        this.inputAdapter.removeEventListener('noteon', this.handleNoteOn);
        this.inputAdapter.removeEventListener('noteoff', this.handleNoteOff);
        this.inputAdapter.removeEventListener('controlchange', this.handleControlChange);
        this.inputAdapter.removeEventListener('message', this.handleMessage);
        this.inputAdapter.close();
    }
    handleMessage = (event) => {
        this.emit('message', this.messageAdapter.convertMessage(event));
    };
    handleNoteOn = (event) => {
        if (this.messageAdapter.getPadMapping(event) == null) {
            this.emit('unknownnote', event);
        }
        const hit = this.messageAdapter.convertNoteOn(event);
        if (hit.value > 0) {
            this.emit('hit', hit);
        }
    };
    handleNoteOff = (event) => {
        const choke = this.messageAdapter.convertNoteOff(event);
        if (choke.value > 0) {
            this.emit('choke', choke);
        }
    };
    handleControlChange = (event) => {
        this.messageAdapter.controlChange(event);
    };
}
export class DrumMessageAdapter {
    clock;
    config;
    _pedalState = 0;
    mapping;
    constructor(clock, config = defaultConfig) {
        this.clock = clock;
        this.config = config;
        this.mapping = DrumMessageAdapter.initMapping(config);
    }
    controlChange = (event) => {
        if (event.control == 4) {
            this.pedalState = event.value;
        }
    };
    get pedalState() {
        return this._pedalState;
    }
    set pedalState(value) {
        this._pedalState = value;
    }
    convertNoteOn = (event) => {
        const padMapping = this.getPadMapping(event);
        return {
            kind: 'hit',
            timestamp: this.clockTime(event),
            note: this.note(event, padMapping),
            value: this.value(event, padMapping)
        };
    };
    convertNoteOff = (event) => {
        const padMapping = this.getPadMapping(event);
        return {
            kind: 'choke',
            timestamp: this.clockTime(event),
            note: this.note(event, padMapping),
            value: this.value(event, padMapping)
        };
    };
    clockTime = (event) => this.clock.timeAt(event.timestamp);
    note = (event, padMapping) => padMapping?.note ?? event.note;
    value = (event, padMapping) => {
        const [min, max] = padMapping?.velocity ?? this.config.velocity ?? [0, 127];
        const clamped = event.velocity < min ? 0 : clamp(event.velocity, min, max);
        return clamp(clamped / max, 0, 1);
    };
    getPadMapping = (event) => this.mapping.get(event.note)?.find(m => m.pedal == null || m.pedal[0] <= this.pedalState && this.pedalState <= m.pedal[1]);
    static initMapping(config) {
        const pads = chain(config.pads)
            .groupBy(pad => pad.pad)
            .map((mappings, pad) => [parseInt(pad), mappings])
            .value();
        return new Map(pads);
    }
    convertMidiEvent = (midiEvent) => {
        switch (midiEvent?.kind) {
            case 'noteon':
                return this.convertNoteOn(midiEvent);
            case 'noteoff':
                return this.convertNoteOff(midiEvent);
            case 'controlchange':
                this.controlChange(midiEvent);
                return null;
            default:
                return null;
        }
    };
    convertMessage = (midiMessage) => {
        return { ...midiMessage, timestamp: Math.round(this.clockTime(midiMessage) * 1000) };
    };
}
//# sourceMappingURL=MidiDrumAdapter.js.map